import { SegmentedControl } from '@byecode/ui'
import type { Field, TypeInstanceMap } from '@lighthouse/core'
import { BACKGROUND_TYPE, EMPTY_VIEW_IMAGE_MODE } from '@lighthouse/core'
import {
    getInputInnerTypeMatch,
    getIsFindUseInRichText,
    Group4ByecodeUi,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    useAtomData,
    useFindUseObjectContext
} from '@lighthouse/shared'
import { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { InheritLabel } from '@/components/InheritLabel'
import { BackgroundField } from '@/components/StyleSetting/components/BackgroundField'
import { FillColor } from '@/components/StyleSetting/components/FillColor'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import type { ViewBreakPointSlice } from '../../../View/formSlice'

const IMAGE_MODE_OPTIONS = [
    {
        label: '默认',
        value: EMPTY_VIEW_IMAGE_MODE.default
    },
    {
        label: '隐藏',
        value: EMPTY_VIEW_IMAGE_MODE.hidden
    },
    {
        label: '自定义',
        value: EMPTY_VIEW_IMAGE_MODE.custom
    }
]

export const EmptySetting = () => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { control, watch } = useFormContext<ViewBreakPointSlice>()
    const { handleSetBreakKey } = useSetExtendsKeys()

    const findUseObject = useFindUseObjectContext()

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()

    const imageMode = watch('breakPoint.emptyView.imageMode')

    return (
        <Group4ByecodeUi label="空状态">
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="空状态文案" name="breakPoint.emptyView.title" />
                <Controller
                    control={control}
                    name="breakPoint.emptyView.title"
                    render={({ field }) => {
                        const isHighLight = getIsFindUseInRichText({
                            doc: field.value,
                            findUseObject
                        })
                        return (
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                highlighting={isHighLight}
                                style={{ width: 180 }}
                                autoHeight
                                placeholder="请输入"
                                viewOptions={customViewOption ? [customViewOption] : undefined}
                                pageOptions={pageOptions}
                                userOption={userOption}
                                value={field.value}
                                dataSourceList={dataSourceList}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.emptyView.title')
                                }}
                                onBlur={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.emptyView.title')
                                }}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="文案颜色" name="breakPoint.emptyView.titleColor" />
                <Controller
                    control={control}
                    name="breakPoint.emptyView.titleColor"
                    render={({ field }) => (
                        <FillColor
                            enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                            value={field.value}
                            onChange={val => {
                                field.onChange(val)
                                handleSetBreakKey('breakPoint.emptyView.titleColor')
                            }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="空状态插画" name="breakPoint.emptyView.imageMode" />
                <Controller
                    control={control}
                    name="breakPoint.emptyView.imageMode"
                    render={({ field }) => (
                        <SegmentedControl
                            style={{ width: 180 }}
                            fullWidth
                            data={IMAGE_MODE_OPTIONS}
                            value={field.value}
                            onChange={val => {
                                field.onChange(val)
                                handleSetBreakKey('breakPoint.emptyView.imageMode')
                            }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>
            {imageMode === EMPTY_VIEW_IMAGE_MODE.custom && (
                <ListItem4ByecodeUi justifyContent="flex-end">
                    <Controller
                        control={control}
                        name="breakPoint.emptyView.customImage"
                        render={({ field }) => (
                            <BackgroundField
                                enabledBackgroundTypes={[BACKGROUND_TYPE.image]}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.emptyView.customImage')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}
        </Group4ByecodeUi>
    )
}
